import React from 'react';

interface HomeProps {
  onNavigate: (page: string) => void;
}

const Home: React.FC<HomeProps> = ({ onNavigate }) => {
  return (
    <div className="space-y-24 pb-20">
      {/* Hero Section */}
      <section className="relative h-[750px] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-slate-900">
          <img 
            src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1920&q=80" 
            alt="Dental Tech" 
            className="absolute inset-0 w-full h-full object-cover opacity-60 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-white animate-fade-in">
          <div className="max-w-3xl space-y-8">
            <div className="inline-flex items-center space-x-2 bg-blue-600/20 backdrop-blur-xl border border-blue-400/30 px-4 py-2 rounded-full">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></span>
              <span className="text-xs font-black uppercase tracking-widest">Vancouver's #1 Dental Lab Hub</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-black leading-[1.1] tracking-tighter">
              The Standard of <br/>
              <span className="text-blue-400">Precision.</span>
            </h1>
            <p className="text-xl text-slate-300 font-medium leading-relaxed max-w-2xl">
              From high-translucency Zirconia to clinical-grade infection control. Piggy Dental empowers modern practices with the next generation of dental logistics.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 pt-4">
              <button 
                onClick={() => onNavigate('catalog-lab')}
                className="bg-white text-slate-900 px-10 py-5 rounded-2xl font-black hover:bg-slate-50 transition-all text-center shadow-2xl shadow-white/10 hover:-translate-y-1 active:scale-95"
              >
                Explore Lab Solutions
              </button>
              <button 
                onClick={() => onNavigate('catalog-clinic')}
                className="bg-blue-600 text-white px-10 py-5 rounded-2xl font-black hover:bg-blue-700 transition-all text-center shadow-2xl shadow-blue-600/20 hover:-translate-y-1 active:scale-95"
              >
                Clinic Inventory
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
        <div className="fancy-glass p-8 rounded-[2.5rem] shadow-2xl grid md:grid-cols-3 gap-8 text-center border border-white">
          <div className="space-y-2">
            <h4 className="text-3xl font-black text-slate-900">500+</h4>
            <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Active Partners</p>
          </div>
          <div className="space-y-2 border-x border-slate-100">
            <h4 className="text-3xl font-black text-slate-900">24h</h4>
            <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Local Vancouver Delivery</p>
          </div>
          <div className="space-y-2">
            <h4 className="text-3xl font-black text-slate-900">100%</h4>
            <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Quality Certified</p>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-20 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <h3 className="text-blue-600 font-black uppercase tracking-[0.3em] text-xs">Our Heritage</h3>
              <h2 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tighter leading-tight">
                Engineering the <br/>Future of Dentistry
              </h2>
            </div>
            <p className="text-slate-500 text-lg leading-relaxed font-medium">
              Headquartered in the heart of British Columbia, Piggy Dental has evolved from a boutique supplier to the region's most reliable tech-driven partner. We don't just sell tools—we provide the competitive edge.
            </p>
            <div className="grid grid-cols-2 gap-8 pt-4">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                </div>
                <div>
                  <h5 className="font-black text-slate-900 mb-1">High Velocity</h5>
                  <p className="text-xs text-slate-400 font-semibold">Instant local dispatch.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center text-green-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04M12 21.355l7.117-7.117V9.104"/></svg>
                </div>
                <div>
                  <h5 className="font-black text-slate-900 mb-1">Bio-Safe</h5>
                  <p className="text-xs text-slate-400 font-semibold">Certified dental grade.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="relative group">
            <div className="absolute -inset-4 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-[3rem] blur-2xl opacity-10 group-hover:opacity-20 transition-opacity"></div>
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl aspect-[4/5]">
              <img src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=800&q=80" alt="Clinic Lab" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="bg-slate-900 py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/10 blur-[120px] rounded-full -mr-40 -mt-40"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-20 space-y-4">
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tighter">Choose Your Ecosystem</h2>
            <p className="text-slate-400 font-medium max-w-xl mx-auto">Tailored inventories for the creators in the lab and the healers in the clinic.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {/* Lab Card */}
            <div className="group bg-white/5 backdrop-blur-xl border border-white/10 p-12 rounded-[3rem] hover:bg-white/[0.08] transition-all hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-600/20 text-blue-400 rounded-2xl flex items-center justify-center mb-8 shadow-inner">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.642.316a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.823.362l-1.178.944a2 2 0 01-3.048-2.551l1.178-.944a2 2 0 00.547-1.022M10 9V5a2 2 0 00-2-2H6a2 2 0 00-2 2v4a2 2 0 002 2h2a2 2 0 002-2z"/></svg>
              </div>
              <h3 className="text-3xl font-black text-white mb-6">Lab Solutions</h3>
              <p className="text-slate-400 mb-10 text-lg leading-relaxed">
                Precision milling, ultra-translucent Zirconia blocks, and rapid production resins for high-volume technical labs.
              </p>
              <button 
                onClick={() => onNavigate('catalog-lab')}
                className="inline-flex items-center text-blue-400 font-black uppercase tracking-widest text-xs hover:text-blue-300 group-hover:translate-x-2 transition-all"
              >
                Browse Collection 
                <svg className="ml-3 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </button>
            </div>

            {/* Clinic Card */}
            <div className="group bg-white/5 backdrop-blur-xl border border-white/10 p-12 rounded-[3rem] hover:bg-white/[0.08] transition-all hover:-translate-y-2">
              <div className="w-16 h-16 bg-green-600/20 text-green-400 rounded-2xl flex items-center justify-center mb-8 shadow-inner">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04M12 21.355l7.117-7.117V9.104"/></svg>
              </div>
              <h3 className="text-3xl font-black text-white mb-6">Clinic Solutions</h3>
              <p className="text-slate-400 mb-10 text-lg leading-relaxed">
                Sterilization pouches, premium PPE, surface barriers, and surgical essentials for patient-first dental environments.
              </p>
              <button 
                onClick={() => onNavigate('catalog-clinic')}
                className="inline-flex items-center text-green-400 font-black uppercase tracking-widest text-xs hover:text-green-300 group-hover:translate-x-2 transition-all"
              >
                Browse Collection 
                <svg className="ml-3 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </button>
            </div>

            {/* 3D Printing Card */}
            <div className="group bg-white/5 backdrop-blur-xl border border-white/10 p-12 rounded-[3rem] hover:bg-white/[0.08] transition-all hover:-translate-y-2">
              <div className="w-16 h-16 bg-purple-600/20 text-purple-400 rounded-2xl flex items-center justify-center mb-8 shadow-inner">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2 1m0 0l-2-1m2 1v2.5M4 7l2-1M4 7l2 1M4 7v2.5M10 21l2-1m0 0l2 1m-2-1V18.5M8 18l-2 1m2-1l-2-1m2 1v2.5M20 18l-2 1m2-1l-2-1m2 1v2.5"/></svg>
              </div>
              <h3 className="text-3xl font-black text-white mb-6">Metal 3D Printing</h3>
              <p className="text-slate-400 mb-10 text-lg leading-relaxed">
                Premium BEGO-certified metal printing in partnership with JAS Innovation Inc. Precision RPEs, Ortho bands, and frameworks.
              </p>
              <button 
                onClick={() => onNavigate('printing-services')}
                className="inline-flex items-center text-purple-400 font-black uppercase tracking-widest text-xs hover:text-purple-300 group-hover:translate-x-2 transition-all"
              >
                View Services 
                <svg className="ml-3 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
